import { Input } from "../input";
import { Field, FieldError, FieldLabel } from "../field";
import type { SlugCheck } from "@/api/hooks";
import { cn } from "@/lib/utils";

/**
 * Shared editor for a URL name (nickname or slug).
 *
 * Shows the resulting link as the user types. The preview is computed by the
 * client-side mirror of the backend pipeline, so it is advisory: the value the
 * server stores is authoritative and replaces this on save.
 */
export default function UrlNameField({
  id,
  label,
  value,
  onChange,
  check,
  prefix,
  hint,
  placeholder,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  check: SlugCheck;
  /** Everything before the editable segment, e.g. "eventera.app/sam/-/". */
  prefix: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const showError = check.state === "error" && value.length > 0;
  // The normalized form only matters when it differs -- echoing "my-notes"
  // back at someone who typed "my-notes" is noise.
  const showPreview =
    !showError && check.normalized.length > 0;

  return (
    <Field data-invalid={showError || undefined}>
      <FieldLabel
        htmlFor={id}
        className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground"
      >
        {label}
      </FieldLabel>
      <Input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={showError || undefined}
        className="font-mono text-[13px]"
      />
      {showPreview && (
        <p className="break-all font-mono text-[11px] text-muted-foreground">
          {prefix}
          <span
            className={cn(
              "text-foreground",
              check.state === "checking" && "opacity-60",
            )}
          >
            {check.normalized}
          </span>
          {check.state === "checking" && " …"}
        </p>
      )}
      {hint && !showError && (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      )}
      {showError && <FieldError errors={[{ message: check.message || "" }]} />}
    </Field>
  );
}
